import { useState } from 'react';

import { ChevronDown, ChevronRight } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import File from '@/components/ui/file';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { templateSections } from '@/config/templateConfig';
import { DOCUMENT_TYPES } from '@/constants';
import { DETAIL_MODE, DETAIL_TYPE, useDetailMode } from '@/hooks/useDetailMode';
import { cn } from '@/lib/utils';

const InformationView = ({ document, setDocument, template }) => {
  const [expandedSections, setExpandedSections] = useState({});

  const section = templateSections['information'];

  // Date fields auto-populated at release (Effective / Issue / Next Review).
  // Show a * note + footer legend for unreleased docs so users know these
  // aren't input fields yet.
  const isReleased = document?.status === 'RELEASED';

  if (!section || !template?.config) return null;

  const fieldsToShow = [];

  section.items.forEach((field) => {
    if (
      field.required ||
      (template.config && template.config[field.key] === true)
    ) {
      fieldsToShow.push(field);
    }
  });

  const AUTO_DATE_FIELDS = ['effectiveDate', 'issueDate', 'nextPeriodReviewDate'];
  const hasAutoDates = fieldsToShow.some((f) => AUTO_DATE_FIELDS.includes(f.key));

  const handleFieldChange = (fieldKey, value) => {
    setDocument((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleSectionToggle = (fieldKey) => {
    setExpandedSections((prev) => ({
      ...prev,
      [fieldKey]: !prev[fieldKey],
    }));
  };

  const renderFormField = (field) => {
    const isExpanded = expandedSections[field.key];
    const fieldValue = document[field.key] || '';

    return (
      <Card
        className={cn(field.display !== 'inline' && 'col-span-2')}
        key={field.key}
      >
        {field.display === 'collapsible' ? (
          <Collapsible
            onOpenChange={() => handleSectionToggle(field.key)}
            defaultOpen={false}
          >
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer pb-6">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-medium">
                    {field.label}
                    {!isReleased && AUTO_DATE_FIELDS.includes(field.key) && (
                      <span className="text-destructive">*</span>
                    )}
                  </CardTitle>
                  <div className="flex items-center">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <BasicField
                  fieldKey={field.key}
                  fieldValue={fieldValue}
                  handleFieldChange={handleFieldChange}
                  label={field.label}
                  options={field.options}
                  type={field.type}
                />
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        ) : (
          <div className="flex">
            <CardHeader className="pb-6 grow">
              <div className="flex gap-4 items-start justify-between">
                <CardTitle
                  className={cn(
                    field.display !== 'inline' ? 'w-1/4' : 'w-1/2',
                    'shrink-0',
                    'text-base font-medium'
                  )}
                >
                  {field.label}
                  {!isReleased && AUTO_DATE_FIELDS.includes(field.key) && (
                    <span className="text-destructive">*</span>
                  )}
                </CardTitle>
                <CardDescription className="grow">
                  {field.type !== 'static' ? (
                    <div
                      className={cn(field.display === 'inline' && 'text-right')}
                    >
                      <BasicField
                        fieldKey={field.key}
                        fieldValue={fieldValue}
                        handleFieldChange={handleFieldChange}
                        label={field.label}
                        options={field.options}
                        type={field.type}
                      />
                    </div>
                  ) : field.key === 'controlCopy' ? (
                    template.values?.controlCopyStatement
                  ) : field.key === 'confidentiality' ? (
                    template.values?.confidentialityStatement
                  ) : field.key === 'documentType' ? (
                    <div className="text-right">
                      {DOCUMENT_TYPES[template.type]?.label || template.type}
                    </div>
                  ) : field.key === 'serial' ? (
                    <div className="text-right">
                      {document.serial || field.placeholder}
                    </div>
                  ) : field.key === 'nextPeriodReviewDate' ? (
                    <div className="text-right">
                      {document.nextPeriodReviewDate
                        ? new Date(document.nextPeriodReviewDate)
                            .toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })
                            .replace(/ /g, '-')
                        : field.placeholder}
                    </div>
                  ) : field.key === 'issueDate' ? (
                    <div className="text-right">
                      {document.issueDate
                        ? new Date(document.issueDate)
                            .toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })
                            .replace(/ /g, '-')
                        : field.placeholder}
                    </div>
                  ) : field.key === 'effectiveDate' ? (
                    <div className="text-right">
                      {document.effectiveDate
                        ? new Date(document.effectiveDate)
                            .toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })
                            .replace(/ /g, '-')
                        : field.placeholder}
                    </div>
                  ) : (
                    <div className="text-right">{field.placeholder}</div>
                  )}
                </CardDescription>
              </div>
            </CardHeader>
          </div>
        )}
      </Card>
    );
  };

  if (fieldsToShow.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No fields configured for this section
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      {fieldsToShow.map((field) => renderFormField(field))}
      {!isReleased && hasAutoDates && (
        <div className="col-span-2 text-sm text-muted-foreground">
          * These dates will be updated upon the release of this document.
        </div>
      )}
    </div>
  );
};

const BasicField = ({
  fieldKey,
  fieldValue,
  handleFieldChange,
  label,
  options,
  type,
}) => {
  const { mode } = useDetailMode(DETAIL_TYPE.DOCUMENT);

  if (mode === DETAIL_MODE.STATIC) {
    if (/^data:image/.test(fieldValue)) return <img src={fieldValue} />;
    return fieldValue;
  }

  if (type === 'select')
    return (
      <select
        className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background"
        onChange={({ target }) => handleFieldChange(fieldKey, target.value)}
        value={fieldValue}
      >
        <option value="">Select...</option>
        {options?.map(({ label: optLabel, value }) => (
          <option key={value} value={value}>
            {optLabel}
          </option>
        ))}
      </select>
    );

  if (type === 'date')
    return (
      <Input
        maxLength={10}
        onChange={({ target }) => handleFieldChange(fieldKey, target.value)}
        placeholder="dd/mm/yyyy"
        type="date"
        value={fieldValue}
      />
    );

  if (type === 'file')
    return (
      <>
        <File
          onChange={(file) => handleFieldChange(fieldKey, file)}
          value={fieldValue}
        />
        {fieldValue && (
          <img src={fieldValue?.base64 ? fieldValue.base64 : fieldValue} />
        )}
      </>
    );

  if (type === 'textarea')
    return (
      <Textarea
        autoResize
        className="font-mono resize-none"
        onChange={({ target }) => handleFieldChange(fieldKey, target.value)}
        placeholder={`Enter ${label.toLowerCase()} details...`}
        value={fieldValue}
      />
    );

  return (
    <Input
      className="min-w-40"
      onChange={({ target }) => handleFieldChange(fieldKey, target.value)}
      placeholder={`Enter ${label.toLowerCase()} details...`}
      type="text"
      value={fieldValue}
    />
  );
};

export default InformationView;
